import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { buildAuthRedirectPath } from './utils/authRedirect';

export default function StudentSignupPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [grade, setGrade] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) navigate('/student-dashboard', { replace: true });
        });
    }, [navigate]);

    const handleGoogleSignIn = async () => {
        setLoading(true);
        setError('');
        localStorage.setItem('pendingOAuthIntent', 'google-auth');
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: { redirectTo: buildAuthRedirectPath('/google-signup') },
        });
        if (error) { setError('Google Sign-In failed: ' + error.message); setLoading(false); }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        setError('');
        setNotice('');

        if (password.length < 8) {
            setError('Password must be at least 8 characters long.');
            setLoading(false);
            return;
        }

        try {
            const isGmail = email.toLowerCase().endsWith('@gmail.com');

            const { data: authData, error: signUpError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    emailRedirectTo: buildAuthRedirectPath('/student-login'),
                    data: {
                        full_name: fullName,
                        grade,
                        app_role: 'student',
                    },
                },
            });
            if (signUpError) throw signUpError;
            if (!authData.user) throw new Error('User creation failed.');

            // Supabase returns identities:[] (no error) when email confirmation is ON
            // and the email is already registered — detect this "fake success" case.
            if (authData.user.identities && authData.user.identities.length === 0) {
                if (isGmail) {
                    throw new Error(
                        'This Gmail address is already registered. If you signed up with Google, please use the "Sign up with Google" button instead.'
                    );
                } else {
                    throw new Error(
                        'An account with this email already exists. Please log in instead.'
                    );
                }
            }

            // Profile row is created by DB trigger (see supabase/migrations/…students_rls_and_auth_trigger.sql).
            // If email confirmation is off, we also upsert here so signup works before you run the migration.

            // Block signup if this email is already registered as a teacher
            const { data: existingTeacher } = await supabase
                .from('teachers')
                .select('id')
                .eq('user_id', authData.user.id)
                .maybeSingle();

            if (existingTeacher) {
                await supabase.auth.signOut();
                throw new Error(
                    'This email is already registered as a Teacher account. One email can only be used for one role — Teacher or Student — not both.'
                );
            }

            if (authData.session) {
                const { error: upsertError } = await supabase.from('students').upsert(
                    {
                        user_id: authData.user.id,
                        name: fullName,
                        email,
                        grade,
                        role: 'student',
                        avatar_url: authData.user.user_metadata?.avatar_url || null,
                    },
                    { onConflict: 'user_id' }
                );
                if (upsertError) throw upsertError;
            }

            localStorage.setItem('userRole', 'student');
            localStorage.setItem('studentName', fullName);
            localStorage.setItem('studentGrade', grade);

            if (!authData.session) {
                setNotice(
                    'Check your email to confirm your account, then log in. Your profile is created automatically after you confirm.'
                );
                setLoading(false);
                return;
            }

            navigate('/student-dashboard');
        } catch (error: any) {
            let msg: string = error.message || '';
            let errorMessage: string;
            if (
                msg.includes('already registered') ||
                msg.includes('already been registered') ||
                msg.includes('User already registered')
            ) {
                // Check if it might be a Google/OAuth account
                const isGmail = email.toLowerCase().endsWith('@gmail.com');
                if (isGmail) {
                    errorMessage =
                        'This Gmail address is already registered. If you signed up with Google, please use the "Sign up with Google" button instead.';
                } else {
                    errorMessage = 'An account with this email already exists. Please log in.';
                }
            } else if (msg.includes('email') && msg.includes('invalid')) {
                errorMessage = 'Please enter a valid email address.';
            } else if (msg.includes('Password') || msg.includes('password')) {
                errorMessage = msg;
            } else {
                errorMessage = 'Sign up failed: ' + msg;
            }
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page-shell">
            <div className="page-panel auth-card" style={{ boxShadow: '0 20px 55px rgba(15,23,42,0.08)', border: '1px solid rgba(148,163,184,0.16)' }}>
                <div className="auth-header">
                    <div className="auth-icon-badge" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>S</div>
                    <p className="hero-eyebrow" style={{ display: 'inline-block', background: 'rgba(16,185,129,0.1)', color: '#059669', borderColor: 'rgba(16,185,129,0.15)' }}>Student Registration</p>
                    <h1 className="page-title" style={{ textAlign: 'center', marginBottom: '0.75rem' }}>Create Student Account</h1>
                    <p className="auth-subcopy">Build your student profile once so finding tutors, saving progress, and returning to your learning path feels seamless on mobile.</p>
                </div>
                <form className="auth-form" onSubmit={handleSubmit}>
                    {notice && (
                        <div className="auth-feedback success">
                            {notice}
                        </div>
                    )}
                    {error && <div className="auth-feedback error">{error}</div>}
                    <input type="text" id="student-signup-full-name" name="fullName" className="input-field" placeholder="Full Name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
                    <select id="student-signup-grade" name="grade" className="input-field" value={grade} onChange={(e) => setGrade(e.target.value)} required>
                        <option value="" disabled>Select Grade Level</option>
                        <option value="Grade 6">Grade 6</option>
                        <option value="Grade 7">Grade 7</option>
                        <option value="Grade 8">Grade 8</option>
                        <option value="Grade 9">Grade 9</option>
                        <option value="Grade 10">Grade 10</option>
                        <option value="O/L">O/L (Grade 11)</option>
                        <option value="A/L">A/L (Grade 12-13)</option>
                    </select>
                    <input type="email" id="student-signup-email" name="email" className="input-field" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    <input type="password" id="student-signup-password" name="password" className="input-field" placeholder="Password (min 8 characters)" value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <div className="form-actions">
                        <button type="submit" className="btn primary" disabled={loading}>{loading ? 'Creating Account...' : 'Create Account'}</button>
                        <button type="button" className="btn secondary" onClick={handleGoogleSignIn} disabled={loading} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                            Sign up with Google
                        </button>
                        <p className="form-note">Already have an account? <Link to="/student-login" className="page-link">Login</Link></p>
                    </div>
                </form>
            </div>
        </div>
    );
}
